import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { School } from '../types';
import { useAuth } from '../app/providers/AuthProvider';

interface SchoolContextType {
  school: School | null;
  isLoading: boolean;
  isRTL: boolean;
  language: string;
  setSchool: (school: School) => void;
  updateSchool: (data: Partial<School>) => Promise<void>;
}

const SchoolContext = createContext<SchoolContextType | undefined>(undefined);

// Mock school data
const mockSchool: School = {
  id: 1,
  publicId: '01HQXK8J3M5N7P9R2S4T6V8W0X',
  name: 'בית ספר לטכנולוגיה',
  slug: 'tech-school-demo',
  schoolType: 'college',
  ownership: 'private',
  purposes: ['college', 'technical', 'online'],
  isOnlineOnly: false,
  phone: '+972-3-1234567',
  email: 'admin@techschool.co.il',
  website: 'https://techschool.co.il',
  address: {
    line1: 'רחוב הרצל 100',
    city: 'תל אביב',
    country: 'ישראל',
    zipcode: '6120101',
  },
  timezone: 'Asia/Jerusalem',
  branding: {
    primaryColor: '#1A7F64',
    secondaryColor: '#F5A623',
  },
  socialLinks: {
    facebook: 'https://facebook.com/techschool',
    linkedin: 'https://linkedin.com/company/techschool',
    instagram: 'https://instagram.com/techschool',
  },
  operatingHours: {
    activeDays: [0, 1, 2, 3, 4],
    startTime: '08:00:00',
    endTime: '18:00:00',
  },
  semesters: [
    { number: 1, startDate: '2024-09-01', endDate: '2025-01-31' },
    { number: 2, startDate: '2025-02-01', endDate: '2025-06-30' },
  ],
  codeOfConduct: 'קוד התנהגות של בית הספר...',
  visionStatement: 'החזון שלנו הוא להכשיר את הדור הבא של מובילי הטכנולוגיה.',
  status: 'active',
  subscriptionPlan: 'premium',
  settings: {
    language: 'he',
    rtl: true,
    notificationsEnabled: true,
  },
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

export function SchoolProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, currentSchool } = useAuth();
  const [school, setSchool] = useState<School | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadSchool = async () => {
      if (!isAuthenticated || !currentSchool?.id) {
        setSchool(null);
        setIsLoading(false);
        return;
      }

      try {
        // In production, fetch from API
        // const response = await api.get(`/schools/${currentSchool.id}`);

        // Mock loading
        await new Promise(resolve => setTimeout(resolve, 500));
        setSchool(mockSchool);
      } catch (error) {
        console.error('Failed to load school:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSchool();
  }, [isAuthenticated, currentSchool?.id]);

  const updateSchool = async (data: Partial<School>) => {
    if (!school) return;

    try {
      // In production, call API
      // const response = await api.patch(`/schools/${school.id}`, data);
      
      setSchool((prev: School | null) => prev ? { ...prev, ...data } : null);
    } catch (error) {
      throw error;
    }
  };

  const isRTL = school?.settings?.rtl ?? true;
  const language = school?.settings?.language ?? 'he';

  return (
    <SchoolContext.Provider value={{
      school,
      isLoading,
      isRTL,
      language,
      setSchool,
      updateSchool,
    }}>
      {children}
    </SchoolContext.Provider>
  );
}

export function useSchool() {
  const context = useContext(SchoolContext);
  if (context === undefined) {
    throw new Error('useSchool must be used within a SchoolProvider');
  }
  return context;
}
